import {DetectionLoyaltyCard} from "@/notification/interfaces";
import {LoyaltyCardMembership} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {membershipProgress} from "@/loyalty-card-membership/services/membershipProgress";

/** The display state of a loyalty card in the detection popup. */
export type DetectionCardState = "inactive" | "active" | "done";

/** A card paired with its analysis, computed once and shared through props. */
export interface DetectionCardEntry {
    card: DetectionLoyaltyCard;
    analysis: DetectionCardAnalysis;
}

/** Everything the popup needs to render one loyalty card. */
export interface DetectionCardAnalysis {
    state: DetectionCardState;
    /**
     * The card is near its threshold: the reward can already be offered,
     * before the closing sale is captured (same rule as the backend).
     */
    advance: boolean;
    /** The reward shortcut shows: a reward reached, or a stamp card at any count. */
    canOffer: boolean;
    /**
     * Label of the reward shortcut: a product is offered, a points reward
     * (a discount) keeps its generic name.
     */
    rewardLabel: string;
    /** Progress toward the next reward, 0-100. */
    percent: number;
    /** Short "x/y" progression label, when a threshold is known. */
    fillLabel: string | null;
    /** Hint line: remaining purchases/points, reward ready, or the card rule. */
    hint: string;
    /** Two-letter initials of the issuing organization, shown when there is no logo. */
    initials: string;
    /** The sponsor's branding logo, when defined. */
    logoUrl: string | null;
    /** Organization name without the "Laboratoire(s)" prefix. */
    shortName: string;
    /** Gradient color pair for the card avatar: brand colors when defined, hashed palette otherwise. */
    gradient: [string, string];
    /** Whether the card is issued by a grouping (vs a laboratory). */
    isGrouping: boolean;
}

/**
 * Strip the "Laboratoire(s)" prefix so initials and short names read like
 * the brand ("Laboratoire Tena" → "Tena").
 */
const stripLabPrefix = (name: string): string => {
    return name.replace(/^laboratoires?\s+/i, "").trim() || name;
};

/**
 * Fixed gradient pairs (from the design prototype); a card picks one through
 * a stable hash of its organization so colors survive re-renders and sessions.
 */
const GRADIENTS: [string, string][] = [
    ["#20066e", "#7a5cff"],
    ["#1f7a8c", "#5ad0e6"],
    ["#0b6fb8", "#5dcfff"],
    ["#4d8c2b", "#a3e06a"],
    ["#8a5a1f", "#e0b062"],
    ["#1f4f8c", "#6aa8e6"],
    ["#2b2e7a", "#8087ff"],
    ["#c2366b", "#ff8bb0"],
    ["#b03fd6", "#ff80a2"],
    ["#e08a2f", "#ffbc68"]
];

/**
 * Normalize a branding color: bare hex values get their leading `#`,
 * anything else is trusted as-is (CSS color), empty becomes null.
 */
const normalizeColor = (color?: string | null): string | null => {
    const trimmed = color?.trim();

    if (!trimmed) {
        return null;
    }

    return /^[0-9a-f]{3,8}$/i.test(trimmed) ? `#${trimmed}` : trimmed;
};

/**
 * Two-letter initials: first letters of the first two words, or the first
 * two letters of a single-word name.
 */
const resolveInitials = (name: string): string => {
    const words = stripLabPrefix(name).split(/\s+/).filter(Boolean);

    if (words.length >= 2) {
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }

    return (words[0] ?? "").slice(0, 2).toUpperCase();
};

/**
 * Analyze a detection loyalty card into its popup display state.
 *
 * Progress derivation is delegated to the shared {@link membershipProgress}:
 * the detection membership lacks the `loyalty_card` back-reference, so the
 * card itself is grafted on (plus an empty stub when the card is not
 * activated yet). Call from within a `computed` so membership mutations
 * (activation at the counter) re-run the analysis.
 */
export function analyzeDetectionCard(card: DetectionLoyaltyCard): DetectionCardAnalysis {
    const membershipLike = {
        ...(card.membership ?? {point_balance: 0, unit_balance: 0, objectives: []}),
        loyalty_card: card
    } as LoyaltyCardMembership;

    const progress = membershipProgress(membershipLike);

    const identityName = card.organization?.name || card.name;
    const isGrouping = card.organization?.morphable_type === "grouping";
    const inactive = !card.membership;
    const done = !inactive && progress.rewardAvailable;
    const advance = !inactive && !done && progress.nearThreshold;

    /**
     * Product threshold fallback: the membership of a freshly activated card
     * has no objective rows yet, so the lowest card-level tier stands in —
     * it keeps the "0/x" progression readable instead of hiding it.
     */
    const tiers = (card.objectives ?? [])
        .filter((objective) => objective.value > 0)
        .sort((a, b) => a.value - b.value);
    const productThreshold = progress.productThreshold || tiers[0]?.value || 0;
    const ruleTeaser = progress.ruleTeaser ?? tiers[0]?.reward?.teaser ?? null;

    let percent = 0;
    let fillLabel: string | null = null;

    if (progress.isProduct && productThreshold > 0) {
        percent = (progress.productProgress / productThreshold) * 100;
        fillLabel = `${progress.productProgress}/${productThreshold}`;
    } else if (progress.isPoint && progress.pointThreshold > 0) {
        percent = (progress.pointProgress / progress.pointThreshold) * 100;
        fillLabel = `${progress.pointProgress}/${progress.pointThreshold}`;
    }

    const remaining = progress.isProduct
        ? Math.max(0, productThreshold - progress.productProgress)
        : progress.pointRemaining;
    const remainingUnit = progress.isProduct ? "achat" : "point";

    let hint: string;

    if (inactive) {
        hint = ruleTeaser ?? "Programme non activé pour ce patient";
    } else if (done) {
        hint = "Palier atteint — récompense disponible";
    } else if (advance) {
        hint = `Plus que ${remaining} ${remainingUnit}${remaining > 1 ? "s" : ""} — la récompense peut déjà être offerte`;
    } else if (remaining > 0) {
        hint = `Encore ${remaining} ${remainingUnit}${remaining > 1 ? "s" : ""} avant la récompense`;
    } else {
        hint = ruleTeaser ?? "";
    }

    const hash = card.organization?.id
        ?? identityName.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);

    // Brand colors win over the hashed palette; a single color makes a flat fill.
    const brandPrimary = normalizeColor(card.organization?.colors?.primary);
    const brandSecondary = normalizeColor(card.organization?.colors?.secondary);

    return {
        state: inactive ? "inactive" : done ? "done" : "active",
        advance,
        canOffer: !inactive && (done || progress.canOffer),
        rewardLabel: progress.isProduct ? "Offrir un produit" : "Récompense",
        percent: Math.max(0, Math.min(100, Math.round(percent))),
        fillLabel: inactive ? null : fillLabel,
        hint,
        initials: resolveInitials(identityName),
        logoUrl: card.organization?.logo?.trim() || null,
        shortName: stripLabPrefix(identityName),
        gradient: brandPrimary
            ? [brandPrimary, brandSecondary ?? brandPrimary]
            : GRADIENTS[hash % GRADIENTS.length],
        isGrouping
    };
}
